import { useEffect, useMemo, useState } from 'react'
import {
    OreHeading,
    OreSelect,
    OreStack,
    OreText,
} from '@edenredespana/oreneta'
import { Trans, useTranslation } from 'react-i18next'
import { useHelpCenter } from '@/HelpCenter/ui/context'
import { MyTicketsFooter, TicketCard, TicketsEmptyState } from '@/HelpCenter/ui/molecules'
import { HelpCenterTranslations } from '@/HelpCenter/ui/helpCenterTranslations'
import { TicketStatus } from '@/HelpCenter/domain'
import { AlertIcon } from '@/Shared/ui/assets/AlertIcon'

export const MyTicketsList = (): JSX.Element => {
    const { t } = useTranslation()
    const { ticketHistory, getTicketHistory } = useHelpCenter()
    const [statusFilter, setStatusFilter] = useState<string>('')
    const [sortOrder, setSortOrder] = useState<string>('newest')
    const [isLoading, setIsLoading] = useState<boolean>(true)

    useEffect(() => {
        getTicketHistory().finally(() => setIsLoading(false))
    }, [])

    const statuses = [TicketStatus.InProgress, TicketStatus.Resolved]

    const filteredAndSortedTickets = useMemo(() => {
        let result = [...ticketHistory]

        if (statusFilter) {
            result = result.filter(ticket => ticket.status === statusFilter)
        }

        result.sort((a, b) => {
            const dateA = new Date(a.createdDate).getTime()
            const dateB = new Date(b.createdDate).getTime()
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB
        })

        return result
    }, [ticketHistory, statusFilter, sortOrder])

    const countInProgress = useMemo(() => {
        return ticketHistory.filter(ticket => ticket.status === TicketStatus.InProgress).length
    }, [ticketHistory])


    const isFiltersDisabled = ticketHistory.length === 0

    const isSortDisabled = isFiltersDisabled || filteredAndSortedTickets.length <= 1

    const getEmptyStateText = () => {
        if (statusFilter === TicketStatus.InProgress) {
            return t(HelpCenterTranslations.myTickets.emptyStateInProgress)
        }
        if (statusFilter === TicketStatus.Resolved) {
            return t(HelpCenterTranslations.myTickets.emptyStateResolved)
        }
        return t(HelpCenterTranslations.myTickets.emptyState)
    }


    return (
        <OreStack space="medium" placeContent="stretch">
            <OreHeading size="title-sm" style={{ marginBottom: '12px' }}>
                {t(HelpCenterTranslations.myTickets.title)}
            </OreHeading>
            <OreStack direction="row" space="large" placeContent='stretch'>
                <OreSelect
                    label={t(HelpCenterTranslations.myTickets.filters.statusLabel)}
                    value={statusFilter}
                    disabled={isFiltersDisabled}
                    onChange={e => setStatusFilter(e.target.value)}
                >
                    <option value="">
                        {t(HelpCenterTranslations.myTickets.filters.statusAll)} ({ticketHistory.length})
                    </option>
                    {statuses.map(status => (
                        <option key={status} value={status}>
                            {status} ({ticketHistory.filter(t => t.status === status).length})
                        </option>
                    ))}
                </OreSelect>
                <OreSelect
                    label={t(HelpCenterTranslations.myTickets.filters.sortLabel)}
                    value={sortOrder}
                    disabled={isSortDisabled}
                    onChange={e => setSortOrder(e.target.value)}
                >
                    <option value="newest">
                        {t(HelpCenterTranslations.myTickets.filters.sortNewest)}
                    </option>
                    <option value="oldest">
                        {t(HelpCenterTranslations.myTickets.filters.sortOldest)}
                    </option>
                </OreSelect>
            </OreStack>
            {countInProgress > 0 && statusFilter !== TicketStatus.InProgress && !(statusFilter === '' && countInProgress === ticketHistory.length) && <OreStack space="small" placeContent="stretch">
                <OreStack
                    direction="row"
                    placeContent='start'
                    placeItems='start'
                    space="xsmall"
                    style={{
                        backgroundColor: '#FDF3CF',
                        padding: '4px',
                        borderRadius: '100px'
                    }}>
                    <AlertIcon />
                    <OreText size="text-md" tone="warning-600">
                        <Trans
                            i18nKey={countInProgress === 1 ? HelpCenterTranslations.myTickets.warningMessage : HelpCenterTranslations.myTickets.warningMessagePlural}
                            values={{ count: countInProgress }}
                            components={[
                                <a
                                    key="0"
                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    onClick={() => setStatusFilter(TicketStatus.InProgress)}
                                />,
                            ]}
                        />
                    </OreText>
                </OreStack>
            </OreStack>}

            {isLoading ? null : filteredAndSortedTickets.length === 0 ? (
                <TicketsEmptyState text={getEmptyStateText()} />
            ) : (
                <OreStack space="small" placeContent="stretch">
                    {filteredAndSortedTickets.map(ticket => (
                        <TicketCard key={ticket.caseId} ticket={ticket} />
                    ))}
                </OreStack>
            )}
            {filteredAndSortedTickets.length > 0 && <MyTicketsFooter />}
        </OreStack>
    )
}
